import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "./input";
import { cn } from "@/lib/utils";

/**
 * PasswordInput — Input + show/hide eye toggle.
 *
 * Wraps the standard <Input> with a lucide Eye/EyeOff button positioned at
 * the right edge. Toggling flips the input's type between "password" and
 * "text" so the user can verify what they typed (BUG-XXX UAT 2026-05-08 —
 * Eva flagged that mistyped passwords were impossible to diagnose without
 * a reveal toggle; same pattern as Villa Manager).
 *
 * The toggle button:
 *  - is keyboard-focusable (default tab order) and announces its current
 *    state via aria-pressed + aria-label from common:password.show/hide
 *  - has a 40x40 min tap target inside the 44px-tall input field
 *  - never carries a real form value (type=button so it can't submit)
 *
 * Accepts all standard <input> props except `type` (intentionally omitted —
 * a PasswordInput is always password-or-revealed-text by definition).
 */
export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {}

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(({ className, ...props }, ref) => {
  const { t } = useTranslation(["common"]);
  const [revealed, setRevealed] = React.useState(false);
  const Icon = revealed ? EyeOff : Eye;
  const label = revealed ? t("common:password.hide") : t("common:password.show");
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={revealed ? "text" : "password"}
        className={cn("pr-12", className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setRevealed((v) => !v)}
        aria-label={label}
        aria-pressed={revealed}
        title={label}
        className={cn(
          "absolute right-1 top-1/2 -translate-y-1/2",
          "inline-flex items-center justify-center rounded-md",
          "transition-colors hover:bg-[var(--talenta-surface)]",
          "focus-visible:outline-2 focus-visible:outline-offset-2",
        )}
        style={{
          minHeight: "40px",
          minWidth: "40px",
          color: "var(--talenta-text-muted)",
        }}
      >
        <Icon size={18} aria-hidden="true" />
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";
